import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Calendar, ArrowRight, ArrowLeft, Camera } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES } from "../lib/i18n";

export default function Projects() {
  const { t, lang, isRTL } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const filter = searchParams.get("category") || "all";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
  setLoading(true);

  const url = filter === "all"
    ? "/projects"
    : `/projects?category=${filter}`;

  api.get(url)
    .then((r) => {
      console.log("Projects API Response:", r.data);
      setProjects(r.data);
    })
    .finally(() => setLoading(false));
}, [filter]);

  const setFilter = (f) => {
    if (f === "all") searchParams.delete("category");
    else searchParams.set("category", f);
    setSearchParams(searchParams);
  };

  return (
    <div data-testid="projects-page">
      <section className="bg-[#0891b2] text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.3em] text-red-200 mb-4 font-semibold">{t.projects.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight" data-testid="projects-title">{t.projects.title}</h1>
          <p className="text-cyan-100 mt-3 max-w-2xl">{t.projects.subtitle}</p>
        </div>
      </section>

      <section className="sticky top-16 z-30 bg-white border-b border-slate-200 py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} data-testid="filter-all"
                  className={`px-4 py-2 text-xs font-medium rounded-full transition-colors ${
                    filter === "all" ? "bg-[#0891b2] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
            {t.projects.filterAll}
          </button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
                    className={`px-4 py-2 text-xs font-medium rounded-full transition-colors ${
                      filter === c ? "bg-[#0891b2] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}>
              {t.categories[c]}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-slate-100 animate-pulse rounded-lg" />)}
            </div>
          ) : projects.length === 0 ? (
            <p className="text-slate-500 text-center py-20" data-testid="projects-empty">{t.projects.empty}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {projects.map((p, i) => {
                const photoCount = (p.gallery?.length || 0) + (p.image ? 1 : 0);
                return (
                <Link key={p.id} to={`/projects/${p.slug}`} data-testid={`project-card-${i}`}
                      className="group bg-white border border-slate-200 hover:border-[#0891b2] hover:shadow-lg transition-all rounded-lg overflow-hidden flex flex-col">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                    <img src={p.image} alt={p.title_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {photoCount > 1 && (
                      <div className="absolute bottom-3 end-3 bg-[#0f172a]/85 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded flex items-center gap-1.5 font-medium">
                        <Camera className="w-3 h-3" />
                        {photoCount} {t.projectExtras.photos}
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[11px] uppercase tracking-widest text-[#ef4444] mb-2">{t.categories[p.category]}</div>
                    <h3 className="font-display text-lg text-slate-900 mb-2 line-clamp-2">{lang === "ar" ? p.title_ar : p.title_en}</h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4 flex-1">{lang === "ar" ? p.description_ar : p.description_en}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />{t.emirates[p.emirate] || (lang === "ar" ? p.location_ar : p.location_en)}
                      </span>
                      {p.completion_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{p.completion_date}</span>}
                    </div>
                    <span className="mt-3 text-[#0891b2] text-xs font-semibold inline-flex items-center gap-1">
                      {t.projects.viewDetails} <Arrow className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
